import type { LayoutMode } from '../../../../shared/constants/layoutBreakpoints';
import { resolveOnboardingElement } from './resolveOnboardingElement';

export type OnboardingTourStepId =
  | 'openSidebar'
  | 'workspace'
  | 'workspaceCreate'
  | 'datasetsSidebar'
  | 'datasetsCreate'
  | 'datasetsMerge'
  | 'boardsSidebar'
  | 'boardsCreate'
  | 'workflowsSidebar'
  | 'workflowsCreate'
  | 'galleriesSidebar'
  | 'profile'
  | 'upload'
  | 'createFolder'
  | 'toolbarFilters'
  | 'viewMode'
  | 'trash'
  | 'mobileMore'
  | 'notifications'
  | 'help';

export type OnboardingTourStepConfig = {
  id: OnboardingTourStepId;
  selector: string;
};

const DESKTOP_MAIN_STEPS: OnboardingTourStepConfig[] = [
  { id: 'upload', selector: '[data-onboarding-root="gallery-toolbar"] [data-onboarding="upload-button"]' },
  { id: 'createFolder', selector: '[data-onboarding-root="gallery-toolbar"] [data-onboarding="create-folder-button"]' },
  { id: 'toolbarFilters', selector: '[data-onboarding="toolbar-filters"]' },
  { id: 'viewMode', selector: '[data-onboarding="view-mode"]' },
  { id: 'trash', selector: '[data-onboarding="trash-button"]' },
  { id: 'notifications', selector: '[data-onboarding="notifications"]' },
  { id: 'help', selector: '[data-onboarding="help-button"]' },
];

const DESKTOP_STEPS: OnboardingTourStepConfig[] = [
  { id: 'workspace', selector: '[data-onboarding="workspace-sidebar"]' },
  { id: 'workspaceCreate', selector: '[data-onboarding="workspace-create"]' },
  { id: 'datasetsSidebar', selector: '[data-onboarding="datasets-sidebar"]' },
  { id: 'datasetsCreate', selector: '[data-onboarding="datasets-create"]' },
  { id: 'datasetsMerge', selector: '[data-onboarding="datasets-merge"]' },
  { id: 'boardsSidebar', selector: '[data-onboarding="boards-sidebar"]' },
  { id: 'boardsCreate', selector: '[data-onboarding="boards-create"]' },
  { id: 'workflowsSidebar', selector: '[data-onboarding="workflows-sidebar"]' },
  { id: 'workflowsCreate', selector: '[data-onboarding="workflows-create"]' },
  { id: 'galleriesSidebar', selector: '[data-onboarding="galleries-sidebar"]' },
  { id: 'profile', selector: '[data-onboarding="sidebar-profile"]' },
  ...DESKTOP_MAIN_STEPS,
];

const TABLET_STEPS: OnboardingTourStepConfig[] = [
  { id: 'openSidebar', selector: '[data-onboarding="sidebar-menu"]' },
  ...DESKTOP_MAIN_STEPS,
];

const MOBILE_STEPS: OnboardingTourStepConfig[] = [
  { id: 'createFolder', selector: '[data-onboarding="mobile-create-folder-button"]' },
  { id: 'upload', selector: '[data-onboarding="mobile-upload-button"]' },
  { id: 'profile', selector: '[data-onboarding="mobile-profile-button"]' },
  { id: 'mobileMore', selector: '[data-onboarding="mobile-more-button"]' },
  { id: 'notifications', selector: '[data-onboarding="notifications"]' },
  { id: 'help', selector: '[data-onboarding="help-button"]' },
];

export const getOnboardingTourSteps = (
  layoutMode: LayoutMode,
): OnboardingTourStepConfig[] => {
  switch (layoutMode) {
    case 'mobile':
      return MOBILE_STEPS;
    case 'tablet':
      return TABLET_STEPS;
    default:
      return DESKTOP_STEPS;
  }
};

export const waitForOnboardingTargets = async (
  selectors: string[],
  attempts = 40,
  delayMs = 100,
): Promise<void> => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const pending = selectors.filter((selector) => !resolveOnboardingElement(selector));
    if (!pending.length) {
      return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
  }
};
